"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Section, { SectionHeader } from "@/components/Section";
import { IconCheck, IconArrowRight } from "@/components/icons";
import CTASection from "@/components/CTASection";
import {
    DASHBOARD_AUTH_URL,
    DASHBOARD_BILLING_URL,
    EARLY_ADOPTER_AUTH_HREF,
} from "@/lib/promotionalOffers";
import { getApiUrl } from "@/lib/api";
import { LOGO_URL, SITE_URL } from "@/lib/site";
import { isValidPricing, type PricingResponse } from "@/lib/pricing";

function formatMoney(amount: number, currency: string) {
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: currency.toUpperCase(),
        minimumFractionDigits: amount % 1 === 0 ? 0 : 2,
        maximumFractionDigits: 2,
    }).format(amount);
}

type PricingTier = {
    name: string;
    price: string;
    priceSub: string;
    sub: string;
    features: string[];
    /** Feature shown with emphasis (e.g. 4× limit on a promo card). */
    highlightFeature?: string;
    cta: string | null;
    ctaHref: string;
    popular: boolean;
    /** Temporary promo styling — distinct from default and popular cards. */
    promo?: boolean;
    badge: string | null;
};

const BASE_FAQ = [
    {
        q: "What happens if I exceed the Developer plan limits?",
        a: "The Developer plan includes up to 50 successful referrals per month. If you exceed this limit, we'll notify you and you can easily upgrade to the Business plan for unlimited referrals.",
    },
    {
        q: "What counts as a successful referral?",
        a: "A successful referral is only counted when the invited user completes your defined conversion event (e.g., starts a paid subscription or makes a purchase). Clicks, invites sent, and free signups don't count towards your limit.",
    },
    {
        q: "Are there any fees on rewards I pay out?",
        a: "No. GrowthRail charges for the engine. The reward itself — credit, payout, free month — runs in your stack (Stripe, RevenueCat, your billing system) via webhook.",
    },
    {
        q: "Do all plans include the SDKs and integrations?",
        a: "Yes. Every plan includes all five first-party SDKs — JavaScript, React, React Native, native iOS (Swift), and native Android (Kotlin) — plus the full REST API. You also get all 15 built-in identity, billing, and subscription integrations on every plan.",
    },
    {
        q: "Can I self-host?",
        a: "Not at launch. The dashboard and API are hosted. We're tracking interest in dedicated/on-prem — ping us if it's a hard requirement.",
    },
];

const EARLY_ADOPTER_FAQ = {
    q: "What is the Early Adopter plan?",
    a: "Early Adopter is a temporary free offer with 200 successful referrals per month. Claim it from this page when you sign up — if you claim it while the offer is open, you keep that higher limit forever.",
};

export default function PricingClient({ initialPricing }: { initialPricing: PricingResponse | null }) {
    const [isAnnual, setIsAnnual] = useState(false);
    const [pricing, setPricing] = useState<PricingResponse | null>(initialPricing);
    const [pricingUnavailable, setPricingUnavailable] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        void fetch(`${getApiUrl()}/public/pricing`, {
            signal: controller.signal,
        })
            .then(async (response) => {
                if (!response.ok) throw new Error("Pricing unavailable");
                const payload: unknown = await response.json();
                if (!isValidPricing(payload))
                    throw new Error("Invalid pricing response");
                setPricing(payload);
            })
            .catch((error: unknown) => {
                if (
                    error instanceof DOMException &&
                    error.name === "AbortError"
                )
                    return;
                setPricingUnavailable(true);
            });

        return () => controller.abort();
    }, []);

    const showEarlyAdopter = pricing?.earlyAdopterOfferOpen === true;
    const selectedBusinessPrice = isAnnual ? pricing?.annual : pricing?.monthly;
    const annualSavingsMonths = pricing
        ? Math.max(
              0,
              (pricing.monthly.amount * 12 - pricing.annual.amount) /
                  pricing.monthly.amount,
          )
        : 0;
    const annualSavingsLabel =
        annualSavingsMonths > 0
            ? `Save ~${annualSavingsMonths.toFixed(1).replace(/\.0$/, "")} mo`
            : "Annual billing";

    const developer: PricingTier = {
        name: "Developer",
        price: "Free",
        priceSub: "",
        sub: "Free forever",
        features: [
            "50 successful referrals / month",
            "All 5 SDKs — JavaScript, React, React Native, Swift + Kotlin",
            "15 built-in auth, billing + subscription integrations",
            "Drop-in referral UI components",
            "Webhooks with retries + delivery logs",
            "Full REST API + OpenAPI reference",
        ],
        cta: "Start free",
        ctaHref: DASHBOARD_AUTH_URL,
        popular: false,
        badge: null,
    };

    const earlyAdopter: PricingTier = {
        name: "Early Adopter",
        price: "Free",
        priceSub: "",
        sub: "Limited-time offer — keep your higher limit forever",
        highlightFeature: "4× more referrals — 200 / month",
        features: [
            "Everything in Developer",
            "Higher limit stays yours forever",
            "All 5 SDKs + 15 built-in integrations",
            "Webhooks with retries + delivery logs",
        ],
        cta: "Claim Early Adopter",
        ctaHref: EARLY_ADOPTER_AUTH_HREF,
        popular: false,
        promo: true,
        badge: "Limited offer",
    };

    const business: PricingTier = {
        name: "Business",
        price: selectedBusinessPrice
            ? formatMoney(
                  selectedBusinessPrice.amount,
                  selectedBusinessPrice.currency,
              )
            : pricingUnavailable
              ? "Unavailable"
              : "Loading…",
        priceSub: isAnnual ? "/yr" : "/mo",
        sub: selectedBusinessPrice
            ? isAnnual
                ? `Billed annually (${annualSavingsLabel})`
                : "Billed monthly"
            : "Live Stripe pricing is temporarily unavailable",
        features: [
            "Everything in Developer",
            "Unlimited successful referrals",
            "Unlimited reward dispatch",
            "Priority support",
        ],
        cta: selectedBusinessPrice
            ? `Start — ${formatMoney(
                  selectedBusinessPrice.amount,
                  selectedBusinessPrice.currency,
              )}${isAnnual ? "/yr" : "/mo"}`
            : null,
        ctaHref: DASHBOARD_BILLING_URL,
        popular: true,
        badge: "Most popular",
    };

    // Permanent plans are Developer + Business; Early Adopter is an optional promo insert.
    const TIERS: PricingTier[] = showEarlyAdopter
        ? [developer, earlyAdopter, business]
        : [developer, business];

    const FAQ = showEarlyAdopter
        ? [
              {
                  q: "What happens if I exceed the free plan limits?",
                  a: "The Developer plan includes up to 50 successful referrals per month; Early Adopter includes 200 while the offer is available. If you exceed your limit, referrals are still tracked but rewards pause until you upgrade to Business for unlimited referrals.",
              },
              EARLY_ADOPTER_FAQ,
              ...BASE_FAQ.slice(1),
          ]
        : BASE_FAQ;

    const permanentOffers = [
        {
            "@type": "Offer",
            name: "Developer Plan",
            price: "0.00",
            priceCurrency: pricing?.monthly.currency.toUpperCase() ?? "GBP",
            url: `${SITE_URL}/pricing`,
        },
        ...(pricing
            ? [
                  {
                      "@type": "Offer",
                      name: "Business Plan (Monthly)",
                      price: pricing.monthly.amount.toFixed(2),
                      priceCurrency: pricing.monthly.currency.toUpperCase(),
                      url: `${SITE_URL}/pricing`,
                  },
                  {
                      "@type": "Offer",
                      name: "Business Plan (Annual)",
                      price: pricing.annual.amount.toFixed(2),
                      priceCurrency: pricing.annual.currency.toUpperCase(),
                      url: `${SITE_URL}/pricing`,
                  },
              ]
            : []),
    ];

    const offers = showEarlyAdopter
        ? [
              permanentOffers[0],
              {
                  "@type": "Offer",
                  name: "Early Adopter Plan",
                  price: "0.00",
                  priceCurrency:
                      pricing?.monthly.currency.toUpperCase() ?? "GBP",
                  url: `${SITE_URL}/pricing`,
              },
              ...permanentOffers.slice(1),
          ]
        : permanentOffers;

    const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: FAQ.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: {
                "@type": "Answer",
                text: item.a,
            },
        })),
    };

    const productSchema = pricing ? {
        "@context": "https://schema.org",
        "@type": "Product",
        name: "GrowthRail",
        image: LOGO_URL,
        description:
            "Universal referral program platform with five first-party SDKs and 15 built-in integrations for B2B SaaS applications.",
        brand: {
            "@type": "Brand",
            name: "GrowthRail",
        },
        offers: {
            "@type": "AggregateOffer",
            priceCurrency: pricing.monthly.currency.toUpperCase(),
            lowPrice: "0",
            highPrice: pricing.annual.amount.toFixed(2),
            offerCount: String(offers.length),
            offers,
        },
    } : null;

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: SITE_URL,
            },
            {
                "@type": "ListItem",
                position: 2,
                name: "Pricing",
                item: `${SITE_URL}/pricing`,
            },
        ],
    };

    const heroDescription = showEarlyAdopter
        ? "Start free on Developer, or claim our limited Early Adopter offer for a higher free limit. Upgrade to Business when you need unlimited referrals."
        : "Start free on Developer and upgrade to Business when you need unlimited referrals.";

    return (
        <main className="relative overflow-hidden pt-28">
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
            />
            {productSchema && (
                <script
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify(productSchema),
                    }}
                />
            )}
            <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    __html: JSON.stringify(breadcrumbSchema),
                }}
            />
            <Section className="!pt-8 !pb-8 sm:!pt-12 sm:!pb-12">
                <SectionHeader
                    as="h1"
                    eyebrow="Pricing"
                    title={
                        <>
                            Simple while we&apos;re small,{" "}
                            <span className="text-gradient-warm">
                                fair when we scale
                            </span>
                        </>
                    }
                    description={heroDescription}
                />

                <div className="mt-10 flex justify-center">
                    <div className="flex items-center rounded-full border border-white/10 bg-[#0a0a0a] p-1 shadow-inner">
                        <button
                            onClick={() => setIsAnnual(false)}
                            className={`rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-200 ${
                                !isAnnual
                                    ? "bg-white text-copper-800 shadow-md"
                                    : "text-zinc-400 hover:text-white"
                            }`}
                        >
                            Monthly
                        </button>
                        <button
                            onClick={() => setIsAnnual(true)}
                            className={`flex items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold transition-all duration-200 ${
                                isAnnual
                                    ? "bg-white text-copper-800 shadow-md"
                                    : "text-zinc-400 hover:text-white"
                            }`}
                        >
                            Annually
                            <span
                                className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${
                                    isAnnual
                                        ? "bg-copper-100 text-copper-700"
                                        : "bg-copper-900/40 text-copper-400"
                                }`}
                            >
                                {annualSavingsLabel}
                            </span>
                        </button>
                    </div>
                </div>
            </Section>

            <Section className="!pt-0 !pb-12 sm:!pb-16">
                <div
                    className={`mx-auto grid gap-5 ${
                        showEarlyAdopter
                            ? "max-w-6xl lg:grid-cols-3"
                            : "max-w-4xl lg:grid-cols-2"
                    }`}
                >
                    {TIERS.map((tier) => {
                        const isPromo = Boolean(tier.promo);
                        const cardTone = tier.popular
                            ? "border-copper-500/40 bg-gradient-to-b from-copper-600 to-copper-700 shadow-[0_0_40px_rgba(184,115,51,0.3)]"
                            : isPromo
                              ? "border-teal-500/35 bg-gradient-to-b from-teal-950/90 via-[#0f1f1c] to-[#0c1614] shadow-[0_0_36px_rgba(45,212,191,0.12)]"
                              : "border-white/[0.06] bg-[#1a1a1a]";
                        const muted = tier.popular
                            ? "text-copper-100"
                            : isPromo
                              ? "text-teal-200/70"
                              : "text-zinc-500";
                        const body = tier.popular
                            ? "text-white"
                            : isPromo
                              ? "text-teal-50"
                              : "text-zinc-300";
                        const checkWrap = tier.popular
                            ? "bg-white/20 text-white"
                            : isPromo
                              ? "border border-teal-400/35 bg-teal-500/15 text-teal-200"
                              : "border border-copper-700/40 bg-copper-900/30 text-copper-300";
                        const ctaClass = tier.popular
                            ? "bg-white text-copper-700 shadow-md hover:bg-copper-50"
                            : isPromo
                              ? "border border-teal-400/40 bg-teal-500/15 text-teal-50 hover:bg-teal-500/25"
                              : "border border-white/10 bg-white/5 text-white hover:bg-white/10";
                        const badgeClass = tier.popular
                            ? "bg-white text-copper-700 shadow-sm"
                            : isPromo
                              ? "bg-teal-400 text-teal-950 shadow-sm"
                              : "bg-white/10 text-zinc-300";

                        return (
                            <div
                                key={tier.name}
                                className={`relative flex flex-col rounded-xl border p-6 sm:p-7 transition-all ${cardTone}`}
                            >
                                <div className="flex items-start justify-between gap-2">
                                    <h3 className="font-display text-2xl font-bold text-white">
                                        {tier.name}
                                    </h3>
                                    {tier.badge && (
                                        <div
                                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${badgeClass}`}
                                        >
                                            {tier.badge}
                                        </div>
                                    )}
                                </div>
                                <div className="mt-5 flex items-baseline gap-1">
                                    <span
                                        className={`font-display text-5xl font-bold ${
                                            tier.popular
                                                ? "text-white"
                                                : isPromo
                                                  ? "text-teal-200"
                                                  : "text-gradient-warm"
                                        }`}
                                    >
                                        {tier.price}
                                    </span>
                                    {tier.priceSub && (
                                        <span
                                            className={`text-sm font-medium ${muted}`}
                                        >
                                            {tier.priceSub}
                                        </span>
                                    )}
                                </div>
                                <p className={`mt-1 text-xs ${muted}`}>
                                    {tier.sub}
                                </p>

                                {tier.highlightFeature && (
                                    <div
                                        className={`mt-5 rounded-lg border px-3.5 py-3 ${
                                            isPromo
                                                ? "border-teal-400/30 bg-teal-500/10"
                                                : "border-white/10 bg-white/5"
                                        }`}
                                    >
                                        <p
                                            className={`text-sm font-semibold leading-snug ${
                                                isPromo
                                                    ? "text-teal-100"
                                                    : "text-white"
                                            }`}
                                        >
                                            <span
                                                className={`mr-1.5 inline-flex items-center rounded-md px-1.5 py-0.5 text-[11px] font-bold tracking-wide ${
                                                    isPromo
                                                        ? "bg-teal-400 text-teal-950"
                                                        : "bg-white/15 text-white"
                                                }`}
                                            >
                                                4×
                                            </span>
                                            {tier.highlightFeature.replace(
                                                /^4×\s*/,
                                                "",
                                            )}
                                        </p>
                                    </div>
                                )}

                                <ul className="mt-7 space-y-3 flex-grow">
                                    {tier.features.map((f) => (
                                        <li
                                            key={f}
                                            className="flex items-start gap-2.5"
                                        >
                                            <span
                                                className={`mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full ${checkWrap}`}
                                            >
                                                <IconCheck className="h-3 w-3" />
                                            </span>
                                            <span className={`text-sm ${body}`}>
                                                {f}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                {tier.cta && (
                                    <Link
                                        href={tier.ctaHref}
                                        className={`mt-8 inline-flex w-full items-center justify-center gap-1.5 rounded-xl px-4 py-3 text-sm font-medium transition-all ${ctaClass}`}
                                    >
                                        {tier.cta}
                                        <IconArrowRight className="h-3.5 w-3.5" />
                                    </Link>
                                )}
                            </div>
                        );
                    })}
                </div>

                <div className="mx-auto mt-6 max-w-4xl rounded-xl border border-white/[0.07] bg-white/[0.02] px-5 py-4 text-center">
                    <p className="text-sm font-medium text-zinc-200">
                        Every plan includes five first-party SDKs and all 15 built-in integrations.
                    </p>
                    <p className="mt-1.5 text-xs leading-relaxed text-zinc-500">
                        JavaScript, React, React Native, native iOS (Swift), and native Android (Kotlin) — plus Clerk, Stripe, RevenueCat, WorkOS, Paddle, PayPal, and more.
                    </p>
                </div>
            </Section>

            <Section className="bg-[#121212]">
                <div className="mx-auto max-w-3xl">
                    <SectionHeader
                        eyebrow="Pricing FAQ"
                        title="Frequently asked questions"
                    />

                    <div className="mt-12 divide-y divide-white/5 overflow-hidden rounded-2xl border border-white/[0.06] bg-[#0a0a0a]">
                        {FAQ.map((f, i) => (
                            <details key={i} className="group">
                                <summary className="flex cursor-pointer list-none items-start justify-between gap-4 px-5 py-5 sm:px-7">
                                    <span className="font-display text-base font-semibold text-white sm:text-lg">
                                        {f.q}
                                    </span>
                                    <span className="mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-white/10 bg-white/[0.02] text-copper-300 transition-transform group-open:rotate-45">
                                        <svg
                                            className="h-3 w-3"
                                            viewBox="0 0 20 20"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                        >
                                            <path
                                                d="M10 4v12M4 10h12"
                                                strokeLinecap="round"
                                            />
                                        </svg>
                                    </span>
                                </summary>
                                <div className="px-5 pb-6 text-sm leading-relaxed text-zinc-400 sm:px-7 sm:text-[15px]">
                                    {f.a}
                                </div>
                            </details>
                        ))}
                    </div>
                </div>
            </Section>

            <CTASection
                eyebrow="Start building today"
                title="Ready to launch your referral program?"
                description="Get your API keys and start tracking referrals in minutes."
            />
        </main>
    );
}
